import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle,
  Loader2,
  Upload,
  User,
  UserCheck,
  AlertCircle,
} from 'lucide-react';
import {
  useNeedsProfileSetup,
  useUpdateProfile,
  useUploadAvatar,
  useUser,
} from '../hooks/queries/useAuth';
import { cn } from '../lib/utils';
import { getUserAvatarUrl, getUserDisplayName } from '../lib/user-utils';
import { logger } from '../utils/logger';
import { secureApi } from '../api/secure';
import { useInitialLoadingScreenManager } from '../hooks/useInitialLoadingScreenManager';
import { useQueryClient } from '@tanstack/react-query';
import { projectKeys } from '../hooks/queries/projectKeys';
import { validateImageFile, getAllowedImageFormatsDisplay } from '../utils/imageValidation';

export function ProfilePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: user } = useUser();
  const { mutateAsync: updateProfile } = useUpdateProfile();
  const { mutateAsync: uploadAvatar } = useUploadAvatar();
  const { needsProfileSetup } = useNeedsProfileSetup();
  const queryClient = useQueryClient();

  // Check if this is a required setup (first login)
  const isSetupRequired = location.state?.setupRequired || needsProfileSetup;

  // Get invitation data if it exists in router state
  const invitationId = location.state?.invitationId;
  const projectId = location.state?.projectId;

  const [displayName, setDisplayName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [imageValidationError, setImageValidationError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // State for first day of week preference
  const [firstDayOfWeek, setFirstDayOfWeek] = useState<'monday' | 'sunday'>('monday');

  // Helper to map first day of week to locale
  const getLocaleFromFirstDay = (firstDay: 'monday' | 'sunday') => {
    return firstDay === 'monday' ? 'en-GB' : 'en-US';
  };

  // Helper to map locale to first day of week
  const getFirstDayFromLocale = (locale?: string) => {
    return locale === 'en-US' ? 'sunday' : 'monday';
  };

  // Get loading screen manager hook
  const { markRouteComplete } = useInitialLoadingScreenManager();

  // Initialize display name and preferences from user metadata
  useEffect(() => {
    if (user) {
      const currentDisplayName = getUserDisplayName(user);
      // Only set non-empty display names
      if (currentDisplayName && currentDisplayName.trim() !== '') {
        setDisplayName(currentDisplayName);
      }

      // Set first day of week preference based on locale
      if (user.user_metadata.locale) {
        setFirstDayOfWeek(getFirstDayFromLocale(user.user_metadata.locale));
      } else if (user.user_metadata.first_day_of_week) {
        // For backwards compatibility
        setFirstDayOfWeek(user.user_metadata.first_day_of_week as 'monday' | 'sunday');
      }

      // Mark profile page as loaded
      markRouteComplete();
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate input
    if (!displayName.trim()) {
      setError('Please enter a display name');
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      // Update the user's profile data
      await updateProfile({
        display_name: displayName.trim(),
        locale: getLocaleFromFirstDay(firstDayOfWeek),
      });

      // If this was the initial setup, add a little delay
      if (isSetupRequired) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Process pending invitation if it exists
      if (invitationId && projectId) {
        // Use console.log for debugging in production
        console.log('DEBUG PROFILE - Processing invitation after profile setup', {
          invitationId,
          projectId,
          userId: user?.id,
          email: user?.email,
          timestamp: new Date().toISOString(),
        });

        logger.component('Processing invitation after profile setup', {
          invitationId,
          projectId,
        });

        try {
          // Call the edge function to accept the invitation
          console.log('DEBUG PROFILE - Calling acceptInvitation edge function');
          const result = await secureApi.acceptInvitation(invitationId, projectId);

          console.log('DEBUG PROFILE - Invitation edge function result:', result);
          logger.component('Invitation accepted via edge function', result);

          // Add a small delay to ensure database changes propagate
          console.log('DEBUG PROFILE - Waiting for database changes to propagate');
          await new Promise(resolve => setTimeout(resolve, 1000));

          // Refresh projects to include the newly shared one
          console.log('DEBUG PROFILE - Fetching updated projects list');
          await queryClient.invalidateQueries({ queryKey: projectKeys.lists() });

          // Navigate to the specific project
          console.log('DEBUG PROFILE - Navigating to project:', projectId);
          navigate(`/project/${projectId}`);
          return; // Exit early since we're redirecting
        } catch (invitationError) {
          console.error('DEBUG PROFILE - Error accepting invitation:', {
            message:
              invitationError instanceof Error ? invitationError.message : String(invitationError),
            error: invitationError,
          });
          logger.error('Error accepting invitation:', invitationError);
          // Continue to projects page if invitation processing fails
        }
      }

      // If no invitation or if processing failed, go to the main projects page
      navigate('/project');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update profile';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type and size
    const validation = validateImageFile(file);
    if (!validation.valid) {
      setImageValidationError(validation.error || 'Invalid file');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setIsLoading(true);
    setError('');
    setImageValidationError(null);

    try {
      await uploadAvatar(file);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload avatar');
    } finally {
      setIsLoading(false);
    }
  };

  // Get avatar URL from user metadata
  const avatarUrl = getUserAvatarUrl(user);

  // Handle case where user is not logged in
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <p>Please log in to view your profile</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
      <div className="max-w-2xl mx-auto px-4 py-8 w-full">
        {!isSetupRequired && (
          <button
            onClick={() => {
              // Try to go back in history, but if there's no history (e.g. opened in new tab), go to projects
              if (window.history.length > 1) {
                navigate(-1);
              } else {
                navigate('/project'); // Default fallback to projects page
              }
            }}
            className="flex items-center text-gray-400 hover:text-white mb-8"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back
          </button>
        )}

        <div className="bg-gray-800 rounded-lg p-8">
          {isSetupRequired ? (
            <div className="mb-8 text-center">
              {/* Simple big green check mark */}
              <div className="mb-8 flex justify-center">
                <div className="w-24 h-24 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-20 h-20 text-green-500" />
                </div>
              </div>
              <h1 className="text-2xl font-bold mb-2">Welcome to Zentime!</h1>
              <p className="text-gray-400">Let's get you set up.</p>
            </div>
          ) : (
            <h1 className="text-2xl font-bold mb-8">Edit Profile</h1>
          )}

          <div className="flex flex-col items-center mb-8">
            <div
              className="relative w-32 h-32 rounded-full bg-gray-700 mb-4 cursor-pointer group"
              onClick={() => fileInputRef.current?.click()}
            >
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt="Profile"
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <div className="w-full h-full rounded-full flex items-center justify-center">
                  <User size={48} className="text-gray-400" />
                </div>
              )}
              <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Upload size={24} className="text-white" />
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              onChange={handleFileChange}
              className="hidden"
            />
            {imageValidationError && (
              <div className="flex items-center gap-2 mt-2 text-red-400 text-sm">
                <AlertCircle size={14} className="flex-shrink-0" />
                <span>{imageValidationError}</span>
              </div>
            )}
            <p className="text-sm text-gray-400">
              Click to upload your photo ({getAllowedImageFormatsDisplay()}, max 10MB)
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Full Name</label>
              <input
                type="text"
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                className="w-full bg-gray-700 text-white rounded-lg px-4 py-2 outline-none"
                placeholder="Enter your full name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Calendar Preferences
              </label>
              <div className="bg-gray-700 rounded-lg p-4">
                <p className="text-gray-300 text-sm mb-3">First day of week</p>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="firstDayOfWeek"
                      value="monday"
                      checked={firstDayOfWeek === 'monday'}
                      onChange={() => setFirstDayOfWeek('monday')}
                      className="mr-2 text-blue-500 focus:ring-blue-500"
                    />
                    <span className="text-gray-300">Monday</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="firstDayOfWeek"
                      value="sunday"
                      checked={firstDayOfWeek === 'sunday'}
                      onChange={() => setFirstDayOfWeek('sunday')}
                      className="mr-2 text-blue-500 focus:ring-blue-500"
                    />
                    <span className="text-gray-300">Sunday</span>
                  </label>
                </div>
              </div>
            </div>

            {error && <div className="text-red-500 text-sm">{error}</div>}

            <button
              type="submit"
              disabled={isLoading || !displayName.trim()}
              className={cn(
                'w-full rounded-lg py-2 font-medium transition-colors',
                !displayName.trim()
                  ? 'bg-blue-900 text-gray-300'
                  : 'bg-blue-600 text-white hover:bg-blue-700',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800',
                (isLoading || !displayName.trim()) && 'cursor-not-allowed'
              )}
            >
              {isLoading ? (
                <Loader2 className="animate-spin mx-auto" size={24} />
              ) : isSetupRequired ? (
                <span className="flex items-center justify-center gap-2">
                  <UserCheck size={20} />
                  Complete Setup
                </span>
              ) : (
                'Save Changes'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
